/**
 * Provenance Workspace Context — the shared action layer for opening
 * repository records in the right-hand split pane. Every provenance
 * chip in Ni² responses calls `openProvenance(recordType, recordId)`
 * on this context; the workspace shell renders a single reusable
 * `ProvenanceWorkspacePanel` bound to it.
 *
 * Kept intentionally tiny — no history stack, no tabs, no dock. Only
 * one record can be open at a time. Clicking a second chip replaces
 * the current record in place.
 *
 * Persistence: the right-pane width preference is kept in
 * sessionStorage under `ni2_prov_width_pct` so it survives navigation
 * within the tab without adding a heavier state layer.
 */
import { createContext, useCallback, useContext, useMemo, useState } from "react";

// The categories we know how to render inside ProvenanceWorkspacePanel.
export const KNOWN_RECORD_TYPES = new Set([
  "case", "reflection", "pearl", "mm", "imaging", "literature", "collaboration", "timeline", "unknown",
]);

const ProvenanceWorkspaceContext = createContext(null);

// Sensible clamp values from the brief.
const MIN_LEFT_PCT = 40;
const MIN_RIGHT_PCT = 30;
const DEFAULT_LEFT_PCT = 60; // → right = 40%
const STORAGE_KEY = "ni2_prov_width_pct";

export function ProvenanceWorkspaceProvider({ children }) {
  const [active, setActive] = useState(null); // { type, id }
  const [leftPct, setLeftPctState] = useState(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      const n = stored ? Number(stored) : NaN;
      if (Number.isFinite(n) && n >= MIN_LEFT_PCT && n <= 100 - MIN_RIGHT_PCT) return n;
    } catch {}
    return DEFAULT_LEFT_PCT;
  });

  const setLeftPct = useCallback((pct) => {
    const clamped = Math.min(100 - MIN_RIGHT_PCT, Math.max(MIN_LEFT_PCT, Math.round(pct)));
    setLeftPctState(clamped);
    try { sessionStorage.setItem(STORAGE_KEY, String(clamped)); } catch {}
  }, []);

  const openProvenance = useCallback((recordType, recordId) => {
    if (!recordId) return;
    const type = KNOWN_RECORD_TYPES.has(recordType) ? recordType : "unknown";
    setActive({ type, id: recordId });
  }, []);

  const closeProvenance = useCallback(() => setActive(null), []);

  // Debug hook — expose openProvenance on `window` so QA and demo
  // scripts can trigger the missing-record path (`?prov=BOGUS-ID`) or
  // any specific chip programmatically without walking React internals.
  if (typeof window !== "undefined") {
    window.__ni2OpenProvenance = openProvenance;
  }

  const value = useMemo(
    () => ({
      active,
      openProvenance,
      closeProvenance,
      leftPct,
      rightPct: 100 - leftPct,
      setLeftPct,
      MIN_LEFT_PCT,
      MIN_RIGHT_PCT,
      isOpen: !!active,
    }),
    [active, openProvenance, closeProvenance, leftPct, setLeftPct]
  );

  return (
    <ProvenanceWorkspaceContext.Provider value={value}>
      {children}
    </ProvenanceWorkspaceContext.Provider>
  );
}

export function useProvenanceWorkspace() {
  const ctx = useContext(ProvenanceWorkspaceContext);
  if (!ctx) {
    return {
      active: null,
      openProvenance: () => {},
      closeProvenance: () => {},
      leftPct: DEFAULT_LEFT_PCT,
      rightPct: 100 - DEFAULT_LEFT_PCT,
      setLeftPct: () => {},
      MIN_LEFT_PCT,
      MIN_RIGHT_PCT,
      isOpen: false,
    };
  }
  return ctx;
}

/**
 * Map a chip's `category` (as produced by Ni2StructuredAnswer) OR a
 * bare repository ID to a canonical recordType so callers don't have
 * to duplicate the classifier.
 */
export function classifyProvenanceId(id) {
  if (!id) return "unknown";
  if (/^VS-[A-Z]+-\d+$/i.test(id) || /^CASE-\d+$/i.test(id)) return "case";
  if (/^REF-\d+$/i.test(id)) return "reflection";
  if (/^PEARL-\d+$/i.test(id)) return "pearl";
  if (/^MM(-[A-Z0-9]+)?-\d+$/i.test(id)) return "mm";
  if (/^IMG-\d+$/i.test(id)) return "imaging";
  if (/^LIT-\d+$/i.test(id)) return "literature";
  if (/^TLE-.*$/i.test(id)) return "timeline";
  if (/^COLLAB(-[A-Z0-9]+)?-\d+$/i.test(id)) return "collaboration";
  return "unknown";
}
